import React from 'react';
import L from 'leaflet';
import { MapContainer, Marker, Polyline, TileLayer, useMap, ZoomControl, AttributionControl } from 'react-leaflet';
import { Plus } from 'lucide-react';
import { InlineLoadingState, PageLoadingState } from '../LoadingSpinner';
import 'leaflet/dist/leaflet.css';
import { EmployeeMultiSelect } from '../employees/EmployeeMultiSelect';
import { isFilterMultiSelectNone } from '../filterMultiSelectUtils';
import { ymdDayEndIso, ymdDayStartIso } from '../../utils/activityDateTime';
import { useCompanyTimezone } from '../../hooks/useCompanyTimezone';
import { CalendarViewSwitcher } from './CalendarViewSwitcher';
import { PLANTAFEL_FILTER_TRIGGER_CLASS } from './plantafelToolbarStyles';
import { MapDateNavigator } from './MapDateNavigator';
import { PlantafelDetailDrawer } from './PlantafelDetailDrawer';
import {
  createAppointmentIcon,
  formatAppointmentTimeRange,
  formatMapSubtitleDate,
  MAP_FALLBACK_COMPANY_ZOOM,
  MAP_FALLBACK_COUNTRY_CENTER,
  MAP_FALLBACK_COUNTRY_ZOOM,
  MAP_SINGLE_MARKER_ZOOM,
  MAP_TILE_ATTRIBUTION,
  MAP_TILE_URL,
  offsetLatLng,
  type MapDefaultCenter,
} from './mapMarkers';
import { stopLeafletMapSafely, syncLeafletViewport } from './mapViewport';
import {
  activityOnDay,
  assignedToEmployee,
  extraAssigneeCount,
  fetchEmployeeRoutes,
  groupStopsByEmployee,
  isMappableActivity,
  localDayKeyFromDate,
  primaryEmployeeForActivity,
  type EmployeeRoute,
} from './mapRoutes';
import type { BoardActivity, BoardEmployee, PlantafelCreateDraft } from './plantafelTypes';

type LeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
  site_address?: string | null;
};
type MemberOption = { user_id: string; email: string; display_name: string | null };
type ActivityTypeOption = { id: string; label: string; icon?: string };

const TOOLBAR_TEXT = 'text-xs font-medium';

type PlantafelMapViewProps = {
  instancePrefix: string;
  leads: LeadOption[];
  activityTypes: ActivityTypeOption[];
  instanceMembers: MemberOption[];
  onLeadCreated?: (lead: LeadOption) => void;
  employeeFilter?: string[];
  onEmployeeFilterChange?: (next: string[]) => void;
};

function MapViewportSync({
  markerPositions,
  fallbackCenter,
  fallbackZoom,
}: {
  markerPositions: [number, number][];
  fallbackCenter: [number, number];
  fallbackZoom: number;
}) {
  const map = useMap();
  React.useEffect(() => {
    syncLeafletViewport(
      map,
      markerPositions,
      fallbackCenter,
      fallbackZoom,
      MAP_SINGLE_MARKER_ZOOM,
    );
    return () => {
      stopLeafletMapSafely(map);
    };
  }, [map, markerPositions, fallbackCenter, fallbackZoom]);
  return null;
}

function formatDayLabel(d: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

export function PlantafelMapView({
  instancePrefix,
  leads,
  activityTypes,
  instanceMembers,
  onLeadCreated,
  employeeFilter: controlledEmployeeFilter,
  onEmployeeFilterChange,
}: PlantafelMapViewProps) {
  const timeZone = useCompanyTimezone();
  const [focusDate, setFocusDate] = React.useState(() => new Date());
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [employees, setEmployees] = React.useState<BoardEmployee[]>([]);
  const [activities, setActivities] = React.useState<BoardActivity[]>([]);
  const [uncontrolledEmployeeFilter, setUncontrolledEmployeeFilter] = React.useState<string[]>([]);
  const employeeFilter = controlledEmployeeFilter ?? uncontrolledEmployeeFilter;
  const setEmployeeFilter = onEmployeeFilterChange ?? setUncontrolledEmployeeFilter;
  const [drawerActivityId, setDrawerActivityId] = React.useState<string | null>(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [createDraft, setCreateDraft] = React.useState<PlantafelCreateDraft | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [routes, setRoutes] = React.useState<EmployeeRoute[]>([]);
  const [routesLoading, setRoutesLoading] = React.useState(false);
  const [defaultMapCenter, setDefaultMapCenter] = React.useState<MapDefaultCenter | null>(null);

  const dayKey = localDayKeyFromDate(focusDate);

  const loadBoard = React.useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/plantafel/board?from=${encodeURIComponent(ymdDayStartIso(dayKey, timeZone))}&to=${encodeURIComponent(ymdDayEndIso(dayKey, timeZone))}`,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Karte konnte nicht geladen werden.');
      }
      const data = await res.json();
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
      setActivities(Array.isArray(data.activities) ? data.activities : []);
      const center = data.default_map_center as MapDefaultCenter | undefined;
      if (
        center &&
        Number.isFinite(Number(center.lat)) &&
        Number.isFinite(Number(center.lng)) &&
        (center.source === 'company' || center.source === 'country')
      ) {
        setDefaultMapCenter({
          lat: Number(center.lat),
          lng: Number(center.lng),
          source: center.source,
        });
      } else {
        setDefaultMapCenter({
          lat: MAP_FALLBACK_COUNTRY_CENTER[0],
          lng: MAP_FALLBACK_COUNTRY_CENTER[1],
          source: 'country',
        });
      }
      setHasLoadedOnce(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
      setEmployees([]);
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  }, [dayKey, timeZone]);

  React.useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  const geocodeRefetchScheduledRef = React.useRef<string | null>(null);

  const employeeFilterActive =
    employeeFilter.length > 0 && !isFilterMultiSelectNone(employeeFilter);

  const dayActivities = React.useMemo(
    () =>
      activities.filter((a) => !a.done && activityOnDay(a, dayKey)),
    [activities, dayKey],
  );

  React.useEffect(() => {
    if (isLoading) return;
    const pendingIds = dayActivities
      .filter((a) => {
        const addr = String(a.lead_site_address ?? '').trim();
        if (!addr) return false;
        const lat = a.lead_site_latitude;
        const lng = a.lead_site_longitude;
        return lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng));
      })
      .map((a) => a.id)
      .sort()
      .join(',');
    if (!pendingIds) {
      geocodeRefetchScheduledRef.current = null;
      return;
    }
    const scheduleKey = `${dayKey}:${pendingIds}`;
    if (geocodeRefetchScheduledRef.current === scheduleKey) return;
    geocodeRefetchScheduledRef.current = scheduleKey;
    const timer = window.setTimeout(() => {
      void loadBoard({ silent: true });
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [dayActivities, dayKey, isLoading, loadBoard]);

  const filteredDayActivities = React.useMemo(() => {
    if (!employeeFilterActive) return dayActivities;
    return dayActivities.filter((a) =>
      employeeFilter.some((empId) => assignedToEmployee(a, empId)),
    );
  }, [dayActivities, employeeFilter, employeeFilterActive]);

  const mappableActivities = React.useMemo(
    () => filteredDayActivities.filter(isMappableActivity),
    [filteredDayActivities],
  );

  const unmappedActivities = React.useMemo(
    () =>
      filteredDayActivities.filter(
        (a) => !isMappableActivity(a) || !String(a.lead_site_address ?? '').trim(),
      ),
    [filteredDayActivities],
  );

  const visibleEmployees = React.useMemo(() => {
    if (!employeeFilterActive) return employees;
    const set = new Set(employeeFilter);
    return employees.filter((e) => set.has(e.id));
  }, [employees, employeeFilter, employeeFilterActive]);

  const employeeSelectOptions = React.useMemo(
    () =>
      employees.map((e) => ({
        id: e.id,
        name: (e.name ?? '').trim() || 'Mitarbeiter',
        color_idx: e.color_idx,
        avatarColor: e.color,
        initials: e.initials,
        subtitle: (e.role ?? '').trim() || undefined,
      })),
    [employees],
  );

  React.useEffect(() => {
    let cancelled = false;
    const groups = groupStopsByEmployee(mappableActivities, visibleEmployees);
    const routable = groups.filter((g) => g.stops.length >= 2);
    if (routable.length === 0) {
      setRoutes([]);
      setRoutesLoading(false);
      return;
    }
    setRoutesLoading(true);
    void fetchEmployeeRoutes(routable).then((next) => {
      if (cancelled) return;
      setRoutes(next);
      setRoutesLoading(false);
    });
    return () => {
      cancelled = true;
      setRoutesLoading(false);
    };
  }, [mappableActivities, visibleEmployees]);

  const markerPositions = React.useMemo(
    () => mappableActivities.map((a) => [a.lead_site_latitude, a.lead_site_longitude] as [number, number]),
    [mappableActivities],
  );

  const fallbackMapCenter = React.useMemo<[number, number]>(
    () =>
      defaultMapCenter
        ? [defaultMapCenter.lat, defaultMapCenter.lng]
        : MAP_FALLBACK_COUNTRY_CENTER,
    [defaultMapCenter],
  );

  const fallbackMapZoom =
    defaultMapCenter?.source === 'company' ? MAP_FALLBACK_COMPANY_ZOOM : MAP_FALLBACK_COUNTRY_ZOOM;

  const coordGroups = React.useMemo(() => {
    const map = new Map<string, typeof mappableActivities>();
    for (const activity of mappableActivities) {
      const key = `${activity.lead_site_latitude.toFixed(6)}:${activity.lead_site_longitude.toFixed(6)}`;
      const list = map.get(key) ?? [];
      list.push(activity);
      map.set(key, list);
    }
    return map;
  }, [mappableActivities]);

  const selectedActivity = drawerActivityId
    ? activities.find((a) => a.id === drawerActivityId) ?? null
    : null;

  const appointmentCount = filteredDayActivities.length;
  const isToday = dayKey === localDayKeyFromDate(new Date());
  const subtitleDate = formatMapSubtitleDate(focusDate);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-app-surface">
      <div className="flex w-full shrink-0 flex-wrap items-center gap-3 border-b border-app-border bg-app-surface px-4 py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CalendarViewSwitcher instancePrefix={instancePrefix} active="map" />

          <MapDateNavigator
            value={focusDate}
            onChange={setFocusDate}
            formatLabel={formatDayLabel}
          />

          <p
            className="shrink-0 whitespace-nowrap text-xs font-normal tabular-nums text-app-text-muted"
            aria-live="polite"
          >
            {appointmentCount === 1 ? '1 Termin' : `${appointmentCount} Termine`}{' '}
            {isToday ? 'heute' : 'am'}{' '}
            <span className="text-slate-400">·</span> {subtitleDate}
            {routesLoading ? (
              <span className="ml-2 text-slate-400">Routen werden berechnet…</span>
            ) : null}
          </p>
        </div>

        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3">
          <EmployeeMultiSelect
            employees={employeeSelectOptions}
            selected={employeeFilter}
            onChange={setEmployeeFilter}
            selectionMode="emptyMeansAll"
            showSubtitle
            triggerClassName={PLANTAFEL_FILTER_TRIGGER_CLASS}
          />
          <button
            type="button"
            className="btn-pipedrive-green inline-flex !h-9 shrink-0 items-center gap-1.5 !rounded-md !px-3.5 text-sm font-semibold"
            onClick={() => {
              setDrawerActivityId(null);
              setCreateDraft(null);
              setShowCreate(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Aktivität
          </button>
        </div>
      </div>

      {unmappedActivities.length > 0 ? (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <span className="font-medium">
            {unmappedActivities.length === 1
              ? '1 Termin ohne Adresse'
              : `${unmappedActivities.length} Termine ohne Adresse`}
          </span>
          <span className="text-amber-800">
            {' '}
            —{' '}
            {unmappedActivities
              .slice(0, 3)
              .map((a) => a.title)
              .join(', ')}
            {unmappedActivities.length > 3 ? '…' : ''}
          </span>
        </div>
      ) : null}

      {error && !hasLoadedOnce ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-red-600">{error}</div>
      ) : !hasLoadedOnce ? (
        <PageLoadingState />
      ) : (
        <div className="plantafel-map-container relative min-h-0 flex-1">
          <MapContainer
            center={markerPositions[0] ?? fallbackMapCenter}
            zoom={markerPositions.length > 0 ? MAP_SINGLE_MARKER_ZOOM : fallbackMapZoom}
            className="h-full w-full"
            scrollWheelZoom
            zoomControl={false}
            attributionControl={false}
            zoomAnimation={false}
            fadeAnimation={false}
            markerZoomAnimation={false}
          >
            <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
            <AttributionControl position="bottomleft" prefix={false} />
            <ZoomControl position="bottomright" />
            <MapViewportSync
              markerPositions={markerPositions}
              fallbackCenter={fallbackMapCenter}
              fallbackZoom={fallbackMapZoom}
            />
            {routes.map((route) => (
              <Polyline
                key={route.employeeId}
                positions={route.coordinates}
                pathOptions={{
                  color: route.color,
                  weight: 3,
                  dashArray: '4 7',
                  opacity: 0.9,
                  lineCap: 'round',
                }}
              />
            ))}
            {mappableActivities.map((activity) => {
              const employee = primaryEmployeeForActivity(activity, employees);
              const key = `${activity.lead_site_latitude.toFixed(6)}:${activity.lead_site_longitude.toFixed(6)}`;
              const group = coordGroups.get(key) ?? [activity];
              const indexInGroup = group.findIndex((a) => a.id === activity.id);
              const [lat, lng] = offsetLatLng(
                activity.lead_site_latitude,
                activity.lead_site_longitude,
                indexInGroup,
                group.length,
              );
              const initials = employee?.initials ?? '?';
              const color = employee?.color ?? '#64748b';
              const timeLabel = formatAppointmentTimeRange(activity.due_at, activity.end_at, timeZone);
              return (
                <Marker
                  key={activity.id}
                  position={[lat, lng]}
                  icon={createAppointmentIcon({
                    employee,
                    initials,
                    color,
                    timeLabel,
                    extraAssigneeCount: extraAssigneeCount(activity),
                  })}
                  eventHandlers={{
                    click: (e) => {
                      L.DomEvent.stopPropagation(e.originalEvent);
                      setShowCreate(false);
                      setCreateDraft(null);
                      setDrawerActivityId(activity.id);
                    },
                  }}
                />
              );
            })}
          </MapContainer>

          {isLoading ? (
            <div className="pointer-events-none absolute inset-0 z-[1000] flex items-center justify-center bg-app-surface/60">
              <InlineLoadingState label="Karte wird geladen …" />
            </div>
          ) : error ? (
            <div className="pointer-events-none absolute inset-0 z-[1000] flex items-center justify-center p-6 text-center text-sm text-red-600">
              {error}
            </div>
          ) : mappableActivities.length === 0 ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-app-text-subtle">
              {appointmentCount === 0
                ? 'Keine Termine an diesem Tag.'
                : 'Keine Termine mit gültiger Adresse auf der Karte.'}
            </div>
          ) : null}
        </div>
      )}

      {(drawerActivityId || showCreate) && (
        <PlantafelDetailDrawer
          activity={showCreate ? null : selectedActivity}
          createDraft={showCreate ? createDraft : null}
          leads={leads}
          activityTypes={activityTypes}
          instanceMembers={instanceMembers}
          employees={employees}
          onClose={() => {
            setDrawerActivityId(null);
            setShowCreate(false);
            setCreateDraft(null);
          }}
          onSaved={() => {
            void loadBoard();
            if (showCreate) {
              setShowCreate(false);
              setDrawerActivityId(null);
              setCreateDraft(null);
            }
          }}
          onDeleted={() => {
            void loadBoard();
            setDrawerActivityId(null);
            setShowCreate(false);
            setCreateDraft(null);
          }}
          onLeadCreated={onLeadCreated}
          instancePrefix={instancePrefix}
        />
      )}
    </div>
  );
}
